/**
 * LIGHTSABER SWING STREAK — a short-lived ribbon swept by the light blade during attacks.
 *
 * Every render frame an attack is live, the caller pushes the blade's (base, tip) segment in
 * WORLD (system) space; the ribbon triangulates the last ~0.15 s of segments and fades them
 * by age. ADDITIVE blending with vertex colors means fade = darken (no alpha sorting, no
 * depth writes) — the streak reads as light, exactly like the blade it trails.
 *
 * FLOATING ORIGIN: samples are stored in world space and re-expressed as (world − origin)
 * every update — the fx-module pattern (dmgNums, boostStreams) — so the origin sliding under
 * the player never smears the ribbon.
 *
 * PLANET CARRY (user report 2026-07-22 — overworld streaks "moving outwards very quickly"):
 * on a rotating/orbiting planet the world frame itself moves ~100 m/s under the fighter, so
 * world-space history smears at carry speed. Same cure as boostStreams: when the caller
 * provides a planet frame (setFrame), samples are stored PLANET-LOCAL and re-expressed
 * through the CURRENT pose every rebuild — the streak rides the surface. The lab (no
 * carry) passes nothing and keeps plain world space.
 */
import * as THREE from "three";

/** Ribbon capacity (segments). At 60 fps × 0.15 s life, ~10 live — 24 covers frame spikes. */
const MAX = 24;
/** Seconds a segment lives (the streak's visual length in time). */
const LIFE = 0.15;

const _pB = new THREE.Vector3();
const _pT = new THREE.Vector3();

interface Sample {
  base: THREE.Vector3;
  tip: THREE.Vector3;
  age: number;
}

export class BladeTrail {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.BufferGeometry;
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly samples: Sample[] = [];
  private readonly color = new THREE.Color(0x9be8ff);
  /** Planet pose for planet-local sample storage (null = plain world space). */
  private frame: { center: THREE.Vector3; quat: THREE.Quaternion; invQuat: THREE.Quaternion } | null = null;

  /** Anchor the streak to a (live, interpolated) planet pose — samples store planet-local
   *  and ride the surface. Call every frame BEFORE push/update; pass null off-planet.
   *  Switching space mid-life drops the (≤0.15 s) history: mixed-frame samples smear. */
  setFrame(frame: { center: THREE.Vector3; quat: THREE.Quaternion; invQuat: THREE.Quaternion } | null): void {
    if ((frame === null) !== (this.frame === null)) this.samples.length = 0;
    this.frame = frame;
  }

  constructor() {
    this.geo = new THREE.BufferGeometry();
    this.pos = new Float32Array(MAX * 2 * 3);
    this.col = new Float32Array(MAX * 2 * 3);
    this.geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3));
    this.geo.setAttribute("color", new THREE.BufferAttribute(this.col, 3));
    // Triangle-strip indices over consecutive (base, tip) pairs: two triangles per quad.
    const idx: number[] = [];
    for (let i = 0; i < MAX - 1; i++) {
      const a = i * 2, b = i * 2 + 1, c = i * 2 + 2, d = i * 2 + 3;
      idx.push(a, b, c, b, d, c);
    }
    this.geo.setIndex(idx);
    this.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6); // never culled mid-swing
    this.mesh = new THREE.Mesh(
      this.geo,
      new THREE.MeshBasicMaterial({
        vertexColors: true,
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        fog: false,
      }),
    );
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5; // over the world, with the other additive fx
  }

  /** The streak's tint — call with the blade's accent so they read as one weapon. */
  setColor(c: THREE.Color): void {
    this.color.copy(c).lerp(new THREE.Color(0xffffff), 0.35); // toward white, like the core
  }

  /** Record this frame's blade segment (WORLD space; stored planet-local when a frame is
   *  set — see setFrame). Call only while a swing is live. */
  push(baseWorld: THREE.Vector3, tipWorld: THREE.Vector3): void {
    if (this.samples.length >= MAX) this.samples.pop();
    const base = baseWorld.clone();
    const tip = tipWorld.clone();
    if (this.frame) {
      base.sub(this.frame.center).applyQuaternion(this.frame.invQuat);
      tip.sub(this.frame.center).applyQuaternion(this.frame.invQuat);
    }
    this.samples.unshift({ base, tip, age: 0 });
  }

  /** Age + fade + rebuild. Call every render frame (cheap when empty). */
  update(dt: number, origin: THREE.Vector3): void {
    let n = 0;
    for (const s of this.samples) {
      s.age += dt;
      if (s.age < LIFE) n++;
    }
    this.samples.length = n; // ages are monotone (newest first) — the tail expires together
    if (n < 2) {
      this.mesh.visible = false;
      return;
    }
    this.mesh.visible = true;
    for (let i = 0; i < n; i++) {
      const s = this.samples[i];
      const k = Math.pow(1 - s.age / LIFE, 1.6); // newest brightest, fast falloff
      const o = i * 6;
      // Planet-local history → world via the CURRENT pose (the streak rides the surface).
      _pB.copy(s.base);
      _pT.copy(s.tip);
      if (this.frame) {
        _pB.applyQuaternion(this.frame.quat).add(this.frame.center);
        _pT.applyQuaternion(this.frame.quat).add(this.frame.center);
      }
      this.pos[o] = _pB.x - origin.x; this.pos[o + 1] = _pB.y - origin.y; this.pos[o + 2] = _pB.z - origin.z;
      this.pos[o + 3] = _pT.x - origin.x; this.pos[o + 4] = _pT.y - origin.y; this.pos[o + 5] = _pT.z - origin.z;
      // The base edge dimmer than the tip edge — the classic saber-arc read.
      this.col[o] = this.color.r * k * 0.45; this.col[o + 1] = this.color.g * k * 0.45; this.col[o + 2] = this.color.b * k * 0.45;
      this.col[o + 3] = this.color.r * k; this.col[o + 4] = this.color.g * k; this.col[o + 5] = this.color.b * k;
    }
    this.geo.attributes.position.needsUpdate = true;
    this.geo.attributes.color.needsUpdate = true;
    this.geo.setDrawRange(0, Math.max(0, (n - 1) * 6));
  }
}
